import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { Router } from "@angular/router";

export type ButtonStyle = 'primary' | 'secondary' | 'tertiary' | 'success' | 'successLight' | 'warning' | 'danger' | 'info' | 'link';
export type ButtonType = 'button' | 'submit';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonIconPosition = 'left' | 'right';
export type ButtonWidth = 'wrap' | 'fill';
export type ButtonTextAlign = 'left' | 'center' | 'right';
export type ButtonMargin = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonPadding = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

@Component({
    selector: 'app-button',
    standalone: true,
  imports: [NgIf],
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
})
export class ButtonComponent implements OnInit, OnDestroy {
    @Input() text: string = 'Submit';
    @Input() href: string | undefined;
    @Input() type: ButtonType = 'submit';
    @Input() variant: ButtonStyle = 'primary';
    @Input() size: ButtonSize = 'md';
    @Input() iconPosition: ButtonIconPosition = 'left';
    @Input() width: ButtonWidth = 'wrap';
    @Input() margin: ButtonMargin = 'none';
    @Input() padding: ButtonPadding = 'md';
    @Input() disabled: boolean = false;
    @Input() visible: boolean = true;
    @Input() isBusy: boolean = false;
    @Input() className: string = '';
    @Input() faIcon: string | null = null;
    @Input() confirm: boolean = false;
    @Input() confirmText: string = 'Confirm';
    @Input() iconOnly: boolean = false;
    @Input() textAlign: ButtonTextAlign = 'center';
    @Input() title: string = '';
    @Input() fragment: string | undefined;

    @Output() clicked = new EventEmitter();

    private readonly _router = inject(Router);

    private originalText: string = this.text;
    private originalFaIcon: string | null = this.faIcon;
    protected confirmationActive: boolean = false;
    protected countdown: number = 5;
    private countdownInterval: any;

    ngOnInit() {
        this.originalText = this.text;
        this.originalFaIcon = this.faIcon;
    }

    ngOnDestroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    }

    protected onClick(): void {
        if (this.disabled) return;

        // Handle confirmation logic
        if (this.confirm && !this.confirmationActive) {
            this.startConfirmationCountdown();
        } else {
            this.resetConfirmation();

            if (this.href) {
              this._router.navigate([this.href], this.fragment ? { fragment: this.fragment } : {}).then();
            } else {
              this.clicked.emit(); // Emit the click event if confirmed
            }
        }
    }

    /**
     * Starts the confirmation countdown
     */
    private startConfirmationCountdown(): void {
        this.confirmationActive = true;
        this.originalText = this.text;
        this.originalFaIcon = this.faIcon;
        this.text = `${this.confirmText} (${this.countdown})`;
        this.faIcon = null;

        this.countdownInterval = setInterval(() => {
            this.countdown--;

            if (this.countdown > 0) {
                this.text = `${this.confirmText} (${this.countdown})`;
            } else {
                this.resetConfirmation(); // Reset the button after countdown
            }
        }, 1000);
    }

    /**
     * Resets the confirmation state and restores the original button text
     */
    private resetConfirmation(): void {
        this.confirmationActive = false;
        this.text = this.originalText;
        this.faIcon = this.originalFaIcon;
        this.countdown = 5;

        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }

    protected buttonType(): string {
        return this.type;
    }

    /**
     * Builds and returns a list of class names to apply to the button
     */
    protected classNames(): string {
        let classes = [];

        // Add button style
        classes.push('button button-' + this.variant);

        // Add button size
        classes.push('size-' + this.size);

        // Add margin
        classes.push('margin-' + this.margin);

        // Add padding
        classes.push('padding-' + this.padding);

        // Add custom classes
        if (this.className) classes.push(this.className);

        if (this.confirmationActive) classes.push('px-3');

        if (this.iconOnly) classes.push('icon-only');

        return classes.join(' ');
    }
}
